import React from 'react';
import { Star, Quote, MessageCircle } from 'lucide-react';

const ApexTestimonials = () => {
  const testimonials = [
    {
      id: 'testimonial-1',
      quote: "I used to dread sales calls. Now I actually look forward to them. The process Apex taught me feels completely natural — no pushiness, just genuine conversations that convert.",
      author: 'Sarah Mitchell',
      role: 'Owner, FitSpace Studio',
      location: 'Manchester, UK',
      result: '+£8k MRR'
    },
    {
      id: 'testimonial-2',
      quote: "We were spending thousands on ads but could not convert the leads. Apex helped us increase our close rate by 31% without changing our marketing spend. Game changer.",
      author: 'James Chen',
      role: 'Co-founder, Peak Performance Gym',
      location: 'Bristol, UK',
      result: '+£12k MRR'
    },
    {
      id: 'testimonial-3',
      quote: "The zero objections approach changed everything. When you take people through the right journey, the close happens naturally. Best investment we have made in our business.",
      author: 'Emma Roberts',
      role: 'Director, BodyFit Boutique',
      location: 'Leeds, UK',
      result: '+£6k MRR'
    }
  ];

  return (
    <section id="results" className="relative py-20 md:py-28 bg-[#0d0d0d]">
      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0d1f12]/20 via-transparent to-transparent" />
      
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="text-[#22C55E] text-sm font-medium tracking-[0.2em] uppercase">
            Success Stories
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mt-4">
            Gym Owners Who Transformed Their Sales
          </h2>
          <p className="text-[#A1A1AA] mt-4 max-w-2xl mx-auto">
            Do not just take our word for it — hear from gym owners who went from 
            fearing sales to mastering them.
          </p>
        </div>

        {/* Testimonials grid */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              data-testid={testimonial.id}
              className="group bg-[#121212] border border-[#27272A] rounded-2xl p-8 hover:border-[#22C55E]/50 transition-all duration-300 relative"
            >
              {/* Quote icon */}
              <div className="absolute top-6 right-6 opacity-10">
                <Quote className="w-12 h-12 text-[#22C55E]" />
              </div>

              {/* Stars */}
              <div className="flex gap-1 mb-6">
                {[1, 2, 3, 4, 5].map((starNum) => (
                  <Star key={`star-${testimonial.id}-${starNum}`} className="w-5 h-5 fill-[#22C55E] text-[#22C55E]" />
                ))}
              </div>

              {/* Quote */}
              <blockquote className="text-white leading-relaxed mb-6">
                &ldquo;{testimonial.quote}&rdquo;
              </blockquote>

              {/* Author */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-semibold">{testimonial.author}</p>
                  <p className="text-[#71717A] text-sm">{testimonial.role}</p>
                  <p className="text-[#71717A] text-xs">{testimonial.location}</p>
                </div>
                <div className="bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-full px-3 py-1">
                  <span className="text-[#22C55E] text-sm font-semibold">{testimonial.result}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Personal agency badge */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-6 px-8 py-6 bg-[#121212] border border-[#27272A] rounded-2xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#22C55E]/20 flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-[#22C55E]" />
              </div>
              <div className="text-left">
                <p className="text-white font-semibold">Small Team. Big Results.</p>
                <p className="text-[#71717A] text-sm">Rapid response times, always here to help</p>
              </div>
            </div>
            <div className="hidden sm:block w-px h-12 bg-[#27272A]" />
            <div className="text-center sm:text-left">
              <p className="text-[#A1A1AA] text-sm">Not a faceless agency with one reply a day.</p>
              <p className="text-[#A1A1AA] text-sm">We are focused entirely on <span className="text-[#22C55E] font-semibold">your success</span>.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApexTestimonials;
